package net.bothosting.docs.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.navigation.UndecoratedLinkVariant
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import net.bothosting.docs.toSitePalette
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span

@Page
@Layout(".components.layouts.DocsLayout")
@Composable
fun HomePage() {
    val sitePalette = ColorMode.current.toSitePalette()

    LaunchedEffect(Unit) {
        document.title = "bot-hosting.net Docs"
    }

    Column(Modifier.gap(2.cssRem)) {
        // Heading block
        Column(Modifier.gap(0.5.cssRem)) {
            SpanText(
                "bot-hosting.net",
                Modifier
                    .fontSize(2.5.cssRem)
                    .fontWeight(FontWeight.Bold)
                    .lineHeight(1.2)
                    .color(Colors.White)
            )
            SpanText(
                "Documentation — Guides, tutorials, and references.",
                Modifier
                    .fontSize(1.cssRem)
                    .color(sitePalette.sidebarText)
            )
        }

        // Section cards — 3 columns
        Div(
            Modifier
                .fillMaxWidth()
                .gap(1.cssRem)
                .grid { columns { repeat(3) { size(1.fr) } } }
                .toAttrs()
        ) {
            SectionCard(
                icon = "fa-code",
                title = "Create a Discord Bot",
                description = "Brief explanation of how to create a Discord bot.",
                path = "/create_a_discord_bot/how-to",
            )
            SectionCard(
                icon = "fa-rocket",
                title = "Hosting",
                description = "Tutorials on how to host your application in our site.",
                path = "/hosting_in_bot_hosting_net/create-an-account",
            )
            SectionCard(
                icon = "fa-wrench",
                title = "Troubleshooting",
                description = "Common server/account errors and solutions.",
                path = "/troubleshooting/cannot-find-module-modulenotfounderror-no-such-file-or-directory",
            )
            SectionCard(
                icon = "fa-circle-question",
                title = "FAQ",
                description = "Answers to the most common questions about free servers, coins, payments, and plans.",
                path = "/faqs_and_info/how-to-write-a-support-request",
            )
            SectionCard(
                icon = "fa-users",
                title = "Community",
                description = "Guides and tools contributed by the community: uptime monitors, panels, and useful repos.",
                path = "/community_resources/node-and-website-uptime-monitor",
            )
        }
    }
}

@Composable
private fun SectionCard(icon: String, title: String, description: String, path: String) {
    val sitePalette = ColorMode.current.toSitePalette()
    var isHovered by remember { mutableStateOf(false) }

    Link(
        path,
        variant = UndecoratedLinkVariant,
        modifier = Modifier
            .classNames("section-card-link")
            .onMouseEnter { isHovered = true }
            .onMouseLeave { isHovered = false }
    ) {
        Column(
            Modifier
                .classNames("section-card")
                .gap(0.75.cssRem)
                .padding(1.5.cssRem)
                .backgroundColor(if (isHovered) sitePalette.islandAccent else sitePalette.islandBackground)
                .styleModifier {
                    property("transition", "background-color 0.15s ease")
                }
                .border(1.px, LineStyle.Solid, sitePalette.cardBorder)
                .borderRadius(0.6.cssRem)
                .fillMaxWidth()
        ) {
            Row(Modifier.gap(0.6.cssRem), verticalAlignment = Alignment.CenterVertically) {
                Box(
                    Modifier
                        .width(2.25.cssRem)
                        .height(2.25.cssRem)
                        .borderRadius(0.5.cssRem)
                        .backgroundColor(sitePalette.islandAccent)
                        .styleModifier {
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        }
                ) {
                    Span(
                        Modifier
                            .fontSize(0.9.cssRem)
                            .color(Color.rgb(0x008bf6))
                            .toAttrs { attr("class", "fas $icon") }
                    )
                }
                SpanText(
                    title,
                    Modifier
                        .fontWeight(FontWeight.Bold)
                        .fontSize(1.05.cssRem)
                        .color(Colors.White)
                )
            }
            SpanText(
                description,
                Modifier
                    .fontSize(0.88.cssRem)
                    .color(sitePalette.sidebarText)
                    .lineHeight(1.6)
            )
        }
    }
}
